import { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  LifeBuoy, Plus, Loader2, ArrowLeft, Paperclip, X, Send, MessageSquare,
} from 'lucide-react';
import { toast } from 'sonner';

const CATEGORIES = [
  { id: 'technical_support', label: 'Technical Support' },
  { id: 'feature_request', label: 'Feature Request' },
  { id: 'bug_report', label: 'Bug Report' },
  { id: 'billing', label: 'Billing & Payments' },
  { id: 'account_issues', label: 'Account Issues' },
  { id: 'general_support', label: 'General Support' },
];

const PRIORITIES = [
  { id: 'low', label: 'Low', color: 'bg-zinc-100 text-zinc-700' },
  { id: 'medium', label: 'Medium', color: 'bg-blue-50 text-blue-700' },
  { id: 'high', label: 'High', color: 'bg-amber-50 text-amber-700' },
  { id: 'critical', label: 'Critical', color: 'bg-red-50 text-red-700' },
];

const STATUS_STYLES = {
  open:              { label: 'Open',              color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  in_progress:       { label: 'In Progress',       color: 'bg-blue-50 text-blue-700 border-blue-200' },
  waiting_on_user:   { label: 'Waiting on You',    color: 'bg-amber-50 text-amber-700 border-amber-200' },
  resolved:          { label: 'Resolved',          color: 'bg-zinc-100 text-zinc-700 border-zinc-200' },
  closed:            { label: 'Closed',            color: 'bg-zinc-100 text-zinc-500 border-zinc-200' },
};

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch { return iso; }
};

export const SupportPage = () => {
  const { api, user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [activeTicket, setActiveTicket] = useState(null);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const r = await api.get('/support/tickets');
      setTickets(r.data?.tickets || []);
    } catch (_) {
      toast.error('Failed to load support tickets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTickets(); }, []); // eslint-disable-line

  const filtered = useMemo(() => {
    if (filterStatus === 'all') return tickets;
    return tickets.filter((t) => t.status === filterStatus);
  }, [tickets, filterStatus]);

  const openCount = tickets.filter((t) => !['resolved', 'closed'].includes(t.status)).length;

  return (
    <div className="space-y-6" data-testid="support-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <LifeBuoy className="w-7 h-7 text-indigo-600" />
            Support
          </h1>
          <p className="text-sm text-zinc-600 mt-1">
            Have a question or issue? Open a ticket and our team will get back to you.
          </p>
        </div>
        <Button onClick={() => setShowCreate(true)} data-testid="new-ticket-btn">
          <Plus className="w-4 h-4 mr-2" />
          New ticket
        </Button>
      </div>

      {/* Filters / stats */}
      <Card>
        <CardContent className="p-4 flex items-center gap-3 flex-wrap">
          <span className="text-sm font-medium text-zinc-700">Filter:</span>
          {['all', 'open', 'in_progress', 'waiting_on_user', 'resolved', 'closed'].map((s) => (
            <button
              key={s}
              onClick={() => setFilterStatus(s)}
              data-testid={`filter-status-${s}`}
              className={`text-xs px-3 py-1.5 rounded-full border transition ${
                filterStatus === s
                  ? 'bg-zinc-900 text-white border-zinc-900'
                  : 'bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50'
              }`}
            >
              {s === 'all' ? `All (${tickets.length})` : STATUS_STYLES[s]?.label || s}
            </button>
          ))}
          <span className="ml-auto text-sm text-zinc-500">{openCount} active</span>
        </CardContent>
      </Card>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
        </div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <MessageSquare className="w-10 h-10 mx-auto text-zinc-300 mb-3" />
            <p className="text-zinc-600">
              {tickets.length === 0 ? 'No tickets yet. Open your first one above.' : 'No tickets in this view.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="tickets-list">
          {filtered.map((t) => {
            const st = STATUS_STYLES[t.status] || STATUS_STYLES.open;
            const pr = PRIORITIES.find((p) => p.id === t.priority);
            return (
              <Card
                key={t.id}
                onClick={() => setActiveTicket(t)}
                className="cursor-pointer hover:border-indigo-300 transition"
                data-testid={`ticket-row-${t.id}`}
              >
                <CardContent className="p-4 flex items-start gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-mono text-zinc-400">#{t.number}</span>
                      <h3 className="font-medium text-zinc-900 truncate">{t.subject}</h3>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap text-xs text-zinc-500">
                      <span>{CATEGORIES.find((c) => c.id === t.category)?.label || t.category}</span>
                      <span>·</span>
                      <span>Updated {fmtDate(t.updated_at)}</span>
                      {t.messages?.length > 0 && (
                        <>
                          <span>·</span>
                          <span>{t.messages.length} {t.messages.length === 1 ? 'reply' : 'replies'}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2 shrink-0">
                    <Badge variant="outline" className={st.color}>{st.label}</Badge>
                    {pr && <span className={`text-xs px-2 py-0.5 rounded ${pr.color}`}>{pr.label}</span>}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create modal */}
      <CreateTicketDialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={(t) => {
          setShowCreate(false);
          setTickets((prev) => [t, ...prev]);
          setActiveTicket(t);
        }}
      />

      {/* Detail modal */}
      {activeTicket && (
        <TicketDetailDialog
          ticketId={activeTicket.id}
          isSupportStaff={Boolean(user?.is_admin || user?.role === 'support_staff')}
          onClose={() => { setActiveTicket(null); fetchTickets(); }}
        />
      )}
    </div>
  );
};

// ============== Create ==============

const CreateTicketDialog = ({ open, onClose, onCreated }) => {
  const { api } = useAuth();
  const [subject, setSubject] = useState('');
  const [category, setCategory] = useState('general_support');
  const [priority, setPriority] = useState('medium');
  const [description, setDescription] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (open) {
      setSubject(''); setCategory('general_support'); setPriority('medium');
      setDescription(''); setAttachments([]);
    }
  }, [open]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File exceeds 10 MB limit');
      e.target.value = '';
      return;
    }
    const fd = new FormData();
    fd.append('file', file);
    setUploading(true);
    try {
      const r = await api.post('/support/attachments', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setAttachments((prev) => [...prev, r.data]);
      toast.success(`Uploaded ${file.name}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleSubmit = async () => {
    if (subject.trim().length < 3) { toast.error('Subject must be at least 3 characters'); return; }
    if (description.trim().length < 10) { toast.error('Please describe the issue (10+ chars)'); return; }
    setSubmitting(true);
    try {
      const r = await api.post('/support/tickets', {
        subject: subject.trim(),
        category,
        priority,
        description: description.trim(),
        attachment_ids: attachments.map((a) => a.id),
      });
      toast.success(`Ticket #${r.data.number} created — we'll email you on reply.`);
      onCreated?.(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create ticket');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-xl" data-testid="create-ticket-dialog">
        <DialogHeader>
          <DialogTitle>Open a support ticket</DialogTitle>
          <DialogDescription>Tell us what's going on. We'll reply by email and right here.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label htmlFor="subject">Subject</Label>
            <Input
              id="subject"
              data-testid="ticket-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Brief summary of the issue"
              maxLength={200}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="ticket-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Priority</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger data-testid="ticket-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PRIORITIES.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label htmlFor="desc">Description</Label>
            <Textarea
              id="desc"
              data-testid="ticket-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={6}
              placeholder="Steps to reproduce, expected behavior, anything else that helps us investigate…"
              maxLength={10000}
            />
            <p className="text-xs text-zinc-500 mt-1">{description.length}/10000</p>
          </div>
          <div>
            <Label>Attachments (optional, max 10 MB each)</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {attachments.map((a) => (
                <div key={a.id} className="flex items-center gap-2 bg-zinc-100 rounded px-2 py-1 text-xs">
                  <Paperclip className="w-3 h-3" />
                  <span className="max-w-[140px] truncate">{a.original_name}</span>
                  <button
                    type="button"
                    onClick={() => setAttachments((prev) => prev.filter((x) => x.id !== a.id))}
                    className="text-zinc-500 hover:text-red-600"
                    data-testid={`remove-attachment-${a.id}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                data-testid="upload-attachment-btn"
              >
                {uploading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Paperclip className="w-3 h-3 mr-1" />}
                Attach
              </Button>
              <input
                ref={fileRef}
                type="file"
                hidden
                onChange={handleUpload}
                accept="image/*,application/pdf,text/plain"
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting} data-testid="submit-ticket-btn">
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Submit ticket
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============== Detail ==============

export const TicketDetailDialog = ({ ticketId, isSupportStaff = false, onClose }) => {
  const { api, user } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [reply, setReply] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [posting, setPosting] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.get(`/support/tickets/${ticketId}`);
      setTicket(r.data);
    } catch (e) {
      toast.error('Failed to load ticket');
      onClose?.();
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [ticketId]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('Max 10 MB'); e.target.value = ''; return; }
    const fd = new FormData();
    fd.append('file', file);
    setUploading(true);
    try {
      const r = await api.post('/support/attachments', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setAttachments((p) => [...p, r.data]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleReply = async () => {
    if (reply.trim().length < 1) return;
    setPosting(true);
    try {
      const r = await api.post(`/support/tickets/${ticketId}/reply`, {
        body: reply.trim(),
        attachment_ids: attachments.map((a) => a.id),
      });
      setTicket(r.data);
      setReply('');
      setAttachments([]);
      toast.success('Reply sent');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send reply');
    } finally {
      setPosting(false);
    }
  };

  const setStatus = async (newStatus) => {
    setStatusBusy(true);
    try {
      const r = await api.put(`/support/tickets/${ticketId}/status`, { status: newStatus });
      setTicket(r.data);
      toast.success(`Status: ${newStatus.replace('_', ' ')}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update status');
    } finally {
      setStatusBusy(false);
    }
  };

  const canReply = ticket && ticket.status !== 'closed';
  const ownerCanClose = ticket && !isSupportStaff && ticket.status !== 'closed';

  return (
    <Dialog open={true} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="ticket-detail-dialog">
        {!ticket ? (
          <>
            <DialogHeader>
              <DialogTitle className="sr-only">Loading ticket</DialogTitle>
              <DialogDescription className="sr-only">Loading ticket details…</DialogDescription>
            </DialogHeader>
            <div className="py-16 flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
            </div>
          </>
        ) : (
          <>
            <DialogHeader>
              <button
                type="button"
                onClick={onClose}
                className="text-xs text-zinc-500 flex items-center gap-1 mb-2 hover:text-zinc-900"
              >
                <ArrowLeft className="w-3 h-3" /> Back
              </button>
              <DialogTitle className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-mono text-zinc-400">#{ticket.number}</span>
                <span>{ticket.subject}</span>
              </DialogTitle>
              <DialogDescription className="sr-only">
                Support ticket conversation thread
              </DialogDescription>
              <div className="flex items-center gap-2 flex-wrap text-xs text-zinc-500 mt-1">
                <Badge variant="outline" className={STATUS_STYLES[ticket.status]?.color}>
                  {STATUS_STYLES[ticket.status]?.label || ticket.status}
                </Badge>
                <span>·</span>
                <span>{CATEGORIES.find((c) => c.id === ticket.category)?.label || ticket.category}</span>
                <span>·</span>
                <span>{PRIORITIES.find((p) => p.id === ticket.priority)?.label || ticket.priority}</span>
                <span>·</span>
                <span>Opened {fmtDate(ticket.created_at)}</span>
                {isSupportStaff && ticket.user_email && (
                  <>
                    <span>·</span>
                    <span>From: {ticket.user_email}</span>
                  </>
                )}
              </div>
            </DialogHeader>

            <div className="space-y-3 py-2">
              {/* Original body */}
              <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-3">
                <p className="text-xs font-medium text-zinc-500 mb-1">
                  {ticket.user_name || ticket.user_email} · {fmtDate(ticket.created_at)}
                </p>
                <p className="text-sm text-zinc-800 whitespace-pre-wrap">{ticket.description}</p>
                {ticket.attachments?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {ticket.attachments.map((a) => (
                      <a
                        key={a.id}
                        href={a.url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs flex items-center gap-1 bg-white border border-zinc-200 rounded px-2 py-1 hover:bg-zinc-50"
                      >
                        <Paperclip className="w-3 h-3" /> {a.original_name}
                      </a>
                    ))}
                  </div>
                )}
              </div>

              {/* Thread */}
              {ticket.messages?.map((m) => {
                const mine = m.author_id === user?.id;
                const isSys = m.author_type === 'system';
                if (isSys) {
                  return (
                    <p key={m.id} className="text-xs text-zinc-500 italic text-center">{m.body}</p>
                  );
                }
                return (
                  <div
                    key={m.id}
                    className={`rounded-lg p-3 border ${
                      m.author_type === 'support'
                        ? 'bg-indigo-50 border-indigo-100'
                        : mine
                          ? 'bg-white border-zinc-200 ml-6'
                          : 'bg-zinc-50 border-zinc-200'
                    }`}
                    data-testid={`message-${m.id}`}
                  >
                    <p className="text-xs font-medium text-zinc-500 mb-1">
                      {m.author_type === 'support' ? '🛟 ' : ''}
                      {m.author_name} · {fmtDate(m.created_at)}
                    </p>
                    <p className="text-sm text-zinc-800 whitespace-pre-wrap">{m.body}</p>
                    {m.attachments?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {m.attachments.map((a) => (
                          <a
                            key={a.id}
                            href={a.url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-xs flex items-center gap-1 bg-white border border-zinc-200 rounded px-2 py-1 hover:bg-zinc-50"
                          >
                            <Paperclip className="w-3 h-3" /> {a.original_name}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Reply box */}
            {canReply && (
              <div className="border-t pt-3 space-y-2">
                <Textarea
                  rows={3}
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  placeholder="Write a reply…"
                  data-testid="ticket-reply-input"
                />
                <div className="flex items-center gap-2 flex-wrap">
                  {attachments.map((a) => (
                    <div key={a.id} className="flex items-center gap-2 bg-zinc-100 rounded px-2 py-1 text-xs">
                      <Paperclip className="w-3 h-3" />
                      <span className="max-w-[120px] truncate">{a.original_name}</span>
                      <button
                        type="button"
                        onClick={() => setAttachments((p) => p.filter((x) => x.id !== a.id))}
                        className="text-zinc-500 hover:text-red-600"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                  >
                    {uploading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Paperclip className="w-3 h-3 mr-1" />}
                    Attach
                  </Button>
                  <input
                    ref={fileRef}
                    type="file"
                    hidden
                    onChange={handleUpload}
                    accept="image/*,application/pdf,text/plain"
                  />
                  <Button
                    onClick={handleReply}
                    disabled={posting || reply.trim().length === 0}
                    className="ml-auto"
                    data-testid="send-reply-btn"
                  >
                    {posting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
                    Send reply
                  </Button>
                </div>
              </div>
            )}

            {/* Footer actions */}
            <DialogFooter className="border-t pt-3 flex-wrap gap-2">
              {isSupportStaff && ticket.status !== 'closed' && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setStatus('in_progress')}
                    disabled={statusBusy || ticket.status === 'in_progress'}
                    data-testid="status-in-progress-btn"
                  >
                    Mark In Progress
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setStatus('waiting_on_user')}
                    disabled={statusBusy || ticket.status === 'waiting_on_user'}
                    data-testid="status-waiting-btn"
                  >
                    Awaiting User
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setStatus('resolved')}
                    disabled={statusBusy || ticket.status === 'resolved'}
                    data-testid="status-resolved-btn"
                  >
                    Resolve
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setStatus('closed')}
                    disabled={statusBusy}
                    data-testid="status-closed-btn"
                  >
                    Close
                  </Button>
                </>
              )}
              {ownerCanClose && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setStatus('closed')}
                  disabled={statusBusy}
                  data-testid="user-close-ticket-btn"
                >
                  Close ticket
                </Button>
              )}
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
